import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const compareSortValues = (left, right, direction) => {
  const leftValue = left === null || left === undefined ? '' : left;
  const rightValue = right === null || right === undefined ? '' : right;

  if (typeof leftValue === 'number' && typeof rightValue === 'number') {
    return direction === 'asc' ? leftValue - rightValue : rightValue - leftValue;
  }

  const normalizedLeft = String(leftValue).toLowerCase();
  const normalizedRight = String(rightValue).toLowerCase();

  if (normalizedLeft < normalizedRight) {
    return direction === 'asc' ? -1 : 1;
  }

  if (normalizedLeft > normalizedRight) {
    return direction === 'asc' ? 1 : -1;
  }

  return 0;
};

const sortCollection = (items, sortConfig) => {
  if (!sortConfig?.key) {
    return items;
  }

  return [...items].sort((first, second) => {
    switch (sortConfig.key) {
      case 'participant':
        return compareSortValues(
          first.fullName || first.customerId,
          second.fullName || second.customerId,
          sortConfig.direction
        );
      case 'email':
        return compareSortValues(first.email, second.email, sortConfig.direction);
      case 'gender':
        return compareSortValues(first.gender, second.gender, sortConfig.direction);
      case 'type':
        return compareSortValues(first.customerType, second.customerType, sortConfig.direction);
      case 'status':
        return compareSortValues(first.status, second.status, sortConfig.direction);
      default:
        return 0;
    }
  });
};

const ContestDetails = () => {
  const { contestId } = useParams();
  const { user } = useAuth();
  const [contest, setContest] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [eligibleCustomers, setEligibleCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [removingCustomerId, setRemovingCustomerId] = useState(null);
  const [participantsSort, setParticipantsSort] = useState({ key: 'participant', direction: 'asc' });
  const [eligibleSort, setEligibleSort] = useState({ key: 'participant', direction: 'asc' });

  const isEditor = useMemo(
    () => user?.systemRoles?.some(role => role.code === 'SYSTEM_EDITOR'),
    [user]
  );

  const fetchContestDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [contestResponse, participantsResponse, eligibleResponse] = await Promise.all([
        axios.get(`/api/contests/${contestId}`),
        axios.get(`/api/contests/${contestId}/participants`),
        axios.get(`/api/contests/${contestId}/eligible-customers`)
      ]);
      setContest(contestResponse.data);
      setParticipants(participantsResponse.data || []);
      setEligibleCustomers(eligibleResponse.data || []);
    } catch (err) {
      console.error('Failed to fetch contest details:', err);
      setError('Failed to load challenge details. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [contestId]);

  useEffect(() => {
    if (!contestId) {
      return;
    }
    fetchContestDetails();
  }, [contestId, fetchContestDetails]);

  const toggleCustomer = (customerId) => {
    setSelectedCustomerIds((current) => (
      current.includes(customerId)
        ? current.filter((id) => id !== customerId)
        : [...current, customerId]
    ));
  };

  const handleAddParticipants = async () => {
    try {
      setSaving(true);
      setError('');
      await axios.post(`/api/contests/${contestId}/participants`, { customerIds: selectedCustomerIds });
      setSelectedCustomerIds([]);
      setShowAddModal(false);
      fetchContestDetails();
    } catch (err) {
      console.error('Failed to add contest participants:', err);
      setError(err.response?.data?.message || 'Failed to add users to challenge.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveParticipant = async (participant) => {
    const confirmed = window.confirm(`Remove ${participant.fullName || 'this user'} from the challenge?`);
    if (!confirmed) {
      return;
    }

    try {
      setRemovingCustomerId(participant.customerId);
      setError('');
      await axios.delete(`/api/contests/${contestId}/participants/${participant.customerId}`);
      await fetchContestDetails();
    } catch (err) {
      console.error('Failed to remove contest participant:', err);
      setError(err.response?.data?.message || 'Failed to remove user from challenge.');
    } finally {
      setRemovingCustomerId(null);
    }
  };

  const getTypeBadgeStyle = (customerType) => ({
    background: customerType === 'ONLINE' ? 'var(--primary-blue)' : 'var(--success)',
    color: 'white',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 600
  });

  const getStatusStyle = (status) => {
    switch (status) {
      case 'ACTIVE':
        return { color: 'var(--success)', background: '#F0FFF4' };
      case 'INACTIVE':
        return { color: 'var(--warning)', background: '#FFF7ED' };
      case 'ARCHIVED':
      case 'BLOCKED':
        return { color: 'var(--error)', background: '#FFF5F5' };
      default:
        return { color: 'var(--gray-dark)', background: 'var(--gray-light)' };
    }
  };

  const filteredEligibleCustomers = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) {
      return eligibleCustomers;
    }

    return eligibleCustomers.filter((customer) => (
      customer.fullName?.toLowerCase().includes(normalizedQuery)
      || customer.email?.toLowerCase().includes(normalizedQuery)
      || customer.customerType?.toLowerCase().includes(normalizedQuery)
      || customer.gender?.toLowerCase().includes(normalizedQuery)
      || customer.status?.toLowerCase().includes(normalizedQuery)
    ));
  }, [eligibleCustomers, searchQuery]);

  const sortedParticipants = useMemo(
    () => sortCollection(participants, participantsSort),
    [participants, participantsSort]
  );

  const sortedEligibleCustomers = useMemo(
    () => sortCollection(filteredEligibleCustomers, eligibleSort),
    [filteredEligibleCustomers, eligibleSort]
  );

  const toggleSort = (tableName, key) => {
    const sortState = tableName === 'participants' ? participantsSort : eligibleSort;
    const setter = tableName === 'participants' ? setParticipantsSort : setEligibleSort;

    setter({
      key,
      direction: sortState.key === key && sortState.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  const renderSortableHeader = (label, tableName, key, align = 'left') => {
    const sortState = tableName === 'participants' ? participantsSort : eligibleSort;
    const isActive = sortState.key === key;
    const indicator = isActive ? (sortState.direction === 'asc' ? '↑' : '↓') : '↕';

    return (
      <button
        type="button"
        onClick={() => toggleSort(tableName, key)}
        style={{
          border: 'none',
          background: 'transparent',
          padding: 0,
          margin: 0,
          cursor: 'pointer',
          fontWeight: 700,
          color: 'var(--dark-blue)',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          justifyContent: align === 'center' ? 'center' : 'flex-start'
        }}
      >
        <span>{label}</span>
        <span style={{ fontSize: '12px', color: isActive ? 'var(--primary-blue)' : 'var(--gray-dark)' }}>{indicator}</span>
      </button>
    );
  };

  if (loading) {
    return <div className="page"><div className="loading">Loading challenge...</div></div>;
  }

  if (!contest) {
    return <div className="page"><div className="alert alert-error">Challenge not found.</div></div>;
  }

  return (
    <div className="page">
      <div className="card" style={{ padding: '24px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '240px' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 12px', background: 'var(--pale-blue)', color: 'var(--dark-blue)', borderRadius: '999px', fontSize: '12px', fontWeight: 700, marginBottom: '14px' }}>
              Challenge
            </div>
            <h1 className="page-title" style={{ marginBottom: '8px' }}>{contest.name}</h1>
            <p style={{ color: 'var(--gray-dark)', margin: 0, lineHeight: 1.6 }}>
              Challenge window: <strong>{contest.startDate}</strong> to <strong>{contest.endDate}</strong>
            </p>
          </div>
          {isEditor && (
            <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
              Add Users
            </button>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '14px', marginTop: '20px' }}>
          <div style={{ background: 'var(--gray-light)', border: '1px solid var(--gray-medium)', borderRadius: '10px', padding: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--gray-dark)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>
              Participants
            </div>
            <div style={{ fontSize: '28px', fontWeight: 700, color: 'var(--dark-blue)' }}>
              {participants.length}
            </div>
          </div>
          <div style={{ background: 'var(--gray-light)', border: '1px solid var(--gray-medium)', borderRadius: '10px', padding: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--gray-dark)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>
              Eligible To Add
            </div>
            <div style={{ fontSize: '28px', fontWeight: 700, color: 'var(--dark-blue)' }}>
              {eligibleCustomers.length}
            </div>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error mb-20">{error}</div>}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap', marginBottom: '18px' }}>
          <div>
            <h3 style={{ color: 'var(--dark-blue)', margin: '0 0 6px 0' }}>Participants</h3>
            <p style={{ margin: 0, color: 'var(--gray-dark)', fontSize: '14px' }}>
              Users added to this challenge are listed below with their current profile details.
            </p>
          </div>
          <div style={{ fontSize: '13px', color: 'var(--gray-dark)', background: 'var(--gray-light)', border: '1px solid var(--gray-medium)', borderRadius: '999px', padding: '8px 12px' }}>
            Total: {participants.length}
          </div>
        </div>
        {participants.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '42px 20px', color: 'var(--gray-dark)', background: 'var(--gray-light)', borderRadius: '10px', border: '1px dashed var(--gray-medium)' }}>
            No users have been added to this challenge yet.
          </div>
        ) : (
          <div style={{ overflowX: 'auto', border: '1px solid var(--gray-medium)', borderRadius: '12px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                  <th style={{ padding: '14px 16px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>
                    {renderSortableHeader('Participant', 'participants', 'participant')}
                  </th>
                  <th style={{ padding: '14px 16px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>
                    {renderSortableHeader('Email', 'participants', 'email')}
                  </th>
                  <th style={{ padding: '14px 16px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>
                    {renderSortableHeader('Gender', 'participants', 'gender')}
                  </th>
                  <th style={{ padding: '14px 16px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>
                    {renderSortableHeader('Type', 'participants', 'type')}
                  </th>
                  <th style={{ padding: '14px 16px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>
                    {renderSortableHeader('Status', 'participants', 'status')}
                  </th>
                  {isEditor && (
                    <th style={{ padding: '14px 16px', textAlign: 'center', borderBottom: '2px solid var(--light-blue)' }}>Action</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {sortedParticipants.map((participant, index) => (
                  <tr
                    key={participant.customerId}
                    style={{
                      backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)',
                      borderBottom: '1px solid var(--gray-medium)'
                    }}
                  >
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ fontWeight: 600, color: 'var(--dark-blue)', marginBottom: '4px' }}>
                        {participant.fullName || 'Unnamed user'}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>
                        Customer ID: {participant.customerId}
                      </div>
                    </td>
                    <td style={{ padding: '14px 16px', color: 'var(--gray-dark)' }}>
                      {participant.email || '-'}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{ color: 'var(--dark-blue)', fontWeight: 500 }}>
                        {participant.gender || '-'}
                      </span>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={getTypeBadgeStyle(participant.customerType)}>
                        {participant.customerType || 'N/A'}
                      </span>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{
                        ...getStatusStyle(participant.status),
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '4px 10px',
                        borderRadius: '999px',
                        fontSize: '12px',
                        fontWeight: 700
                      }}>
                        {participant.status || 'UNKNOWN'}
                      </span>
                    </td>
                    {isEditor && (
                      <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                        <button
                          className="btn btn-secondary"
                          onClick={() => handleRemoveParticipant(participant)}
                          disabled={removingCustomerId === participant.customerId}
                          style={{
                            background: removingCustomerId === participant.customerId ? 'var(--gray-medium)' : '#FFF5F5',
                            color: removingCustomerId === participant.customerId ? 'var(--gray-dark)' : 'var(--error)',
                            border: '1px solid #FEB2B2',
                            minWidth: '92px'
                          }}
                        >
                          {removingCustomerId === participant.customerId ? 'Removing...' : 'Remove'}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAddModal && isEditor && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{ background: 'white', width: 'min(900px, 94vw)', maxHeight: '88vh', overflow: 'auto', borderRadius: '16px', padding: '24px', boxShadow: '0 20px 60px rgba(44, 82, 130, 0.18)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h3 style={{ margin: '0 0 6px 0', color: 'var(--dark-blue)' }}>Add Users to Challenge</h3>
                <p style={{ margin: 0, color: 'var(--gray-dark)', fontSize: '14px' }}>
                  Select one or more eligible customers to include in this challenge.
                </p>
              </div>
              <button className="btn btn-secondary" onClick={() => setShowAddModal(false)}>Close</button>
            </div>
            {eligibleCustomers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '42px 20px', color: 'var(--gray-dark)', background: 'var(--gray-light)', borderRadius: '10px', border: '1px dashed var(--gray-medium)' }}>
                No eligible customers are available to add.
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
                  <div style={{ fontSize: '13px', color: 'var(--gray-dark)', background: 'var(--gray-light)', border: '1px solid var(--gray-medium)', borderRadius: '999px', padding: '8px 12px' }}>
                    Selected: {selectedCustomerIds.length}
                  </div>
                  <div style={{ width: 'min(360px, 100%)', position: 'relative' }}>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Search by name, email, type or gender"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      style={{ margin: 0, paddingRight: searchQuery ? '38px' : '12px' }}
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => setSearchQuery('')}
                        style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          border: 'none',
                          background: 'transparent',
                          color: 'var(--gray-dark)',
                          cursor: 'pointer',
                          fontSize: '16px'
                        }}
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>

                <div style={{ border: '1px solid var(--gray-medium)', borderRadius: '12px', overflow: 'hidden' }}>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                      <thead>
                        <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                          <th style={{ width: '64px', padding: '14px 16px', textAlign: 'center', borderBottom: '2px solid var(--light-blue)' }}>Add</th>
                          <th style={{ padding: '14px 16px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>
                            {renderSortableHeader('Customer', 'eligible', 'participant')}
                          </th>
                          <th style={{ padding: '14px 16px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>
                            {renderSortableHeader('Gender', 'eligible', 'gender')}
                          </th>
                          <th style={{ padding: '14px 16px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>
                            {renderSortableHeader('Type', 'eligible', 'type')}
                          </th>
                          <th style={{ padding: '14px 16px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>
                            {renderSortableHeader('Status', 'eligible', 'status')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedEligibleCustomers.map((customer, index) => {
                          const isSelected = selectedCustomerIds.includes(customer.customerId);

                          return (
                            <tr
                              key={customer.customerId}
                              style={{
                                backgroundColor: isSelected
                                  ? '#EBF8FF'
                                  : index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)',
                                borderBottom: '1px solid var(--gray-medium)'
                              }}
                            >
                              <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleCustomer(customer.customerId)}
                                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                />
                              </td>
                              <td style={{ padding: '14px 16px' }}>
                                <div style={{ fontWeight: 600, color: 'var(--dark-blue)', marginBottom: '4px' }}>
                                  {customer.fullName || 'Unnamed user'}
                                </div>
                                <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>
                                  {customer.email || '-'}
                                </div>
                              </td>
                              <td style={{ padding: '14px 16px', color: 'var(--dark-blue)', fontWeight: 500 }}>
                                {customer.gender || '-'}
                              </td>
                              <td style={{ padding: '14px 16px' }}>
                                <span style={getTypeBadgeStyle(customer.customerType)}>
                                  {customer.customerType || 'N/A'}
                                </span>
                              </td>
                              <td style={{ padding: '14px 16px' }}>
                                <span style={{
                                  ...getStatusStyle(customer.status),
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  padding: '4px 10px',
                                  borderRadius: '999px',
                                  fontSize: '12px',
                                  fontWeight: 700
                                }}>
                                  {customer.status || 'UNKNOWN'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {filteredEligibleCustomers.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '24px 16px', color: 'var(--gray-dark)' }}>
                    No eligible customers match the current search.
                  </div>
                )}

                <div style={{ marginTop: '16px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <button className="btn btn-primary" disabled={selectedCustomerIds.length === 0 || saving} onClick={handleAddParticipants}>
                    {saving ? 'Adding...' : 'Add Selected Users'}
                  </button>
                  <button className="btn btn-secondary" onClick={() => setSelectedCustomerIds([])}>
                    Clear Selection
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div style={{ marginTop: '16px' }}>
        <Link to="/contests" className="btn btn-secondary">Back to Challenges</Link>
      </div>
    </div>
  );
};

export default ContestDetails;
